import type { Schemas } from "#shopware";

type LineItem = Schemas["LineItem"];
type OrderLineItem = Schemas["OrderLineItem"];
type Product = Schemas["Product"];
type Cart = Schemas["Cart"];
type Order = Schemas["Order"];

type Ga4Item = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  discount?: number;
  item_brand?: string;
  item_list_name?: string;
  item_list_id?: string;
};

const PURCHASE_KEY = "gtm_purchase_";
const BEGIN_CHECKOUT_KEY = "gtm_begin_checkout_";
const VIEW_ITEM_KEY = "gtm_view_item_";

const excludedLineTypes = new Set([
  "promotion",
  "discount",
  "credit",
  "dsr-line-item-discount",
  "dsr-cart-discount",
]);

const roundMoney = (n: number) =>
  Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

function sumTaxes(taxes?: { tax?: number }[] | null): number {
  if (!taxes?.length) return 0;
  return roundMoney(taxes.reduce((s, t) => s + (t.tax ?? 0), 0));
}

function isSellableCartLine(item: LineItem): boolean {
  return !excludedLineTypes.has(String(item.type));
}

function isSellableOrderLine(item: OrderLineItem): boolean {
  return !excludedLineTypes.has(String(item.type ?? ""));
}

function payloadProductNumber(
  payload: LineItem["payload"] | OrderLineItem["payload"],
): string | undefined {
  if (payload && typeof payload === "object" && "productNumber" in payload) {
    const n = (payload as { productNumber?: string }).productNumber;
    return typeof n === "string" ? n : undefined;
  }
  return undefined;
}

function cartLineToGaItem(item: LineItem): Ga4Item | null {
  if (!isSellableCartLine(item)) return null;
  const unit = item.price?.unitPrice ?? 0;
  const disc = item.price?.listPrice?.discount;
  const discount =
    typeof disc === "number" && disc < 0 ? roundMoney(Math.abs(disc)) : undefined;
  return {
    item_id:
      payloadProductNumber(item.payload) ??
      item.referencedId ??
      item.id,
    item_name: item.label ?? "",
    price: roundMoney(unit),
    quantity: Math.max(0, Math.floor(item.quantity)),
    ...(discount !== undefined ? { discount } : {}),
  };
}

function orderLineToGaItem(item: OrderLineItem): Ga4Item | null {
  if (!isSellableOrderLine(item)) return null;
  const unit = item.unitPrice ?? 0;
  return {
    item_id:
      payloadProductNumber(item.payload) ??
      item.referencedId ??
      item.identifier ??
      item.id,
    item_name: item.label ?? "",
    price: roundMoney(unit),
    quantity: Math.max(0, Math.floor(item.quantity)),
    item_brand: (item.payload as { manufacturerName?: string })?.manufacturerName ?? "",
  };
}

function collectCartItems(cart: Cart): Ga4Item[] {
  const list = cart.lineItems ?? [];
  return list.map(cartLineToGaItem).filter((x): x is Ga4Item => x !== null);
}

function collectOrderItems(order: Order): Ga4Item[] {
  const list = order.lineItems ?? [];
  return list.map(orderLineToGaItem).filter((x): x is Ga4Item => x !== null);
}

function cartCouponCodes(cart: Cart): string | undefined {
  const promos = (cart.lineItems ?? []).filter((i) => i.type === "promotion");
  const codes = promos
    .map((p) => {
      const pl = p.payload as { code?: string } | undefined;
      return pl?.code ?? p.label;
    })
    .filter(Boolean) as string[];
  if (!codes.length) return undefined;
  return codes.join(",");
}

function beginCheckoutDedupeId(cart: Cart): string {
  const token = (cart.token ?? cart.name ?? "").trim();
  if (token) return token;

  const itemSig = (cart.lineItems ?? [])
    .map((item) => {
      const id =
        payloadProductNumber(item.payload) ??
        item.referencedId ??
        item.id;
      const qty = Math.max(0, Math.floor(item.quantity));
      const unit = roundMoney(item.price?.unitPrice ?? 0);
      return `${id}:${qty}:${unit}`;
    })
    .sort()
    .join("|");
  const total = roundMoney(cart.price?.totalPrice ?? 0);
  return `fallback:${total}:${itemSig || "no-items"}`;
}

function shouldSkipRecentEvent(key: string, ttlMs = 1500): boolean {
  if (!import.meta.client) return false;
  try {
    const now = Date.now();
    const previousRaw = sessionStorage.getItem(key);
    const previous = previousRaw ? Number(previousRaw) : 0;
    if (previous && Number.isFinite(previous) && now - previous < ttlMs) {
      return true;
    }
    sessionStorage.setItem(key, String(now));
  } catch {
    // ignore storage issues (private mode / blocked storage)
  }
  return false;
}

function hasSessionFlag(key: string): boolean {
  if (!import.meta.client) return false;
  try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
}

function setSessionFlag(key: string): void {
  if (!import.meta.client) return;
  try { sessionStorage.setItem(key, "1"); } catch { /* */ }
}

export function useGtmTracking() {
  const { sessionContext } = useSessionContext();

  const currencyCode = () =>
    sessionContext.value?.currency?.isoCode ?? "EUR";

  // GTM is always loaded; Consent Mode V2 inside GTM decides whether GA4
  // actually sends data. The app always pushes events to the dataLayer.
  const canPush = () => import.meta.client && !!window.dataLayer;

  const push = (event: string, payload: Record<string, unknown> = {}) => {
    if (!canPush()) return;
    const dl = window.dataLayer ?? (window.dataLayer = []);
    dl.push({ ecommerce: null });
    dl.push({ event, ...payload });
  };

  const trackEvent = (name: string, payload?: Record<string, unknown>) => {
    push(name, payload ?? {});
  };

  const trackFormSuccess = (
    eventName: string,
    metadata?: Record<string, unknown>,
  ) => {
    push(eventName, metadata ?? {});
  };

  const trackOnSuccess = <A extends unknown[], R>(
    eventName: string,
    fn: (...args: A) => Promise<R>,
    metadata?: Record<string, unknown>,
  ) => {
    return async (...args: A): Promise<R> => {
      const result = await fn(...args);
      trackFormSuccess(eventName, metadata);
      return result;
    };
  };

  /**
   * view_item — fired on product detail page
   */
  const trackViewItem = (product: Product) => {
    const dedupeKey = `${VIEW_ITEM_KEY}${product.id}`;
    if (hasSessionFlag(dedupeKey)) return;

    const cur = currencyCode();
    const unit = roundMoney(product.calculatedPrice?.unitPrice ?? 0);

    push("view_item", {
      ecommerce: {
        currency: cur,
        value: unit,
        items: [
          {
            item_id: product.productNumber ?? product.id,
            item_name: product.translated?.name ?? product.name ?? "",
            item_brand: product.manufacturer?.name ?? "",
            price: unit,
            quantity: 1,
          },
        ],
      },
    });
    setSessionFlag(dedupeKey);
  };

  /**
   * view_item_list — fired on category / search listing pages
   */
  const trackViewItemList = (
    products: Product[],
    options: { listName?: string; listId?: string } = {},
  ) => {
    if (!products.length) return;
    const cur = currencyCode();

    const items: Ga4Item[] = products.map((p, index) => ({
      item_id: p.productNumber ?? p.id,
      item_name: p.translated?.name ?? p.name ?? "",
      item_brand: p.manufacturer?.name ?? "",
      price: roundMoney(p.calculatedPrice?.unitPrice ?? 0),
      quantity: 1,
      ...(options.listName ? { item_list_name: options.listName } : {}),
      ...(options.listId ? { item_list_id: options.listId } : {}),
      index,
    }));

    push("view_item_list", {
      ecommerce: {
        currency: cur,
        item_list_name: options.listName ?? "",
        item_list_id: options.listId ?? "",
        items,
      },
    });
  };

  /**
   * add_to_cart — fired when product is added to cart
   */
  const trackAddToCart = (product: Product, qty: number) => {
    const q = Math.max(1, Math.floor(qty));
    const cur = currencyCode();
    const unit = product.calculatedPrice?.unitPrice ?? 0;
    const value = roundMoney(unit * q);

    push("add_to_cart", {
      ecommerce: {
        currency: cur,
        value,
        items: [
          {
            item_id: product.productNumber ?? product.id,
            item_name: product.translated?.name ?? product.name ?? "",
            item_brand: product.manufacturer?.name ?? "",
            price: roundMoney(unit),
            quantity: q,
          },
        ],
      },
    });
  };

  /**
   * remove_from_cart — fired when a line item is removed from cart
   */
  const trackRemoveFromCart = (item: LineItem) => {
    if (!isSellableCartLine(item)) return;
    const ga = cartLineToGaItem(item);
    if (!ga) return;
    const value = roundMoney(ga.price * ga.quantity);

    push("remove_from_cart", {
      ecommerce: {
        currency: currencyCode(),
        value,
        items: [ga],
      },
    });
  };

  /**
   * view_cart — fired when cart page is viewed
   */
  const trackViewCart = (cart: Cart) => {
    const items = collectCartItems(cart);
    if (!items.length) return;

    push("view_cart", {
      ecommerce: {
        currency: currencyCode(),
        value: roundMoney(cart.price?.totalPrice ?? 0),
        items,
      },
    });
  };

  /**
   * begin_checkout — fired on checkout page load
   */
  const trackBeginCheckout = (cart: Cart) => {
    const token = beginCheckoutDedupeId(cart);
    const dedupeKey = `${BEGIN_CHECKOUT_KEY}${token}`;
    if (import.meta.client) {
      try {
        const existing = sessionStorage.getItem(dedupeKey);
        if (existing) return;
      } catch {
        /* private mode */
      }
    }

    const items = collectCartItems(cart);
    if (!items.length) return;

    const cur = currencyCode();
    const value = roundMoney(cart.price?.totalPrice ?? 0);
    const tax = sumTaxes(cart.price?.calculatedTaxes);
    const shipping = roundMoney(
      cart.deliveries?.[0]?.shippingCosts?.totalPrice ?? 0,
    );
    const coupon = cartCouponCodes(cart);

    push("begin_checkout", {
      ecommerce: {
        currency: cur,
        value,
        tax,
        shipping,
        items,
        ...(coupon ? { coupon } : {}),
      },
    });

    if (import.meta.client) {
      try {
        sessionStorage.setItem(dedupeKey, "1");
      } catch {
        /* */
      }
    }
  };

  /**
   * add_shipping_info — fired when user selects a shipping method
   */
  const trackAddShippingInfo = (cart: Cart, shippingTier?: string) => {
    const items = collectCartItems(cart);
    if (!items.length) return;
    const coupon = cartCouponCodes(cart);

    push("add_shipping_info", {
      ecommerce: {
        currency: currencyCode(),
        value: roundMoney(cart.price?.totalPrice ?? 0),
        tax: sumTaxes(cart.price?.calculatedTaxes),
        shipping: roundMoney(cart.deliveries?.[0]?.shippingCosts?.totalPrice ?? 0),
        shipping_tier: shippingTier ?? "",
        items,
        ...(coupon ? { coupon } : {}),
      },
    });
  };

  /**
   * add_payment_info — fired when user selects a payment method
   */
  const trackAddPaymentInfo = (cart: Cart, paymentType?: string) => {
    const items = collectCartItems(cart);
    if (!items.length) return;
    const coupon = cartCouponCodes(cart);

    push("add_payment_info", {
      ecommerce: {
        currency: currencyCode(),
        value: roundMoney(cart.price?.totalPrice ?? 0),
        tax: sumTaxes(cart.price?.calculatedTaxes),
        payment_type: paymentType ?? "",
        items,
        ...(coupon ? { coupon } : {}),
      },
    });
  };

  /**
   * purchase — fired on the order confirmation page
   */
  const trackPurchase = (order: Order) => {
    if (!import.meta.client || !order.id) return;
    const dedupeKey = `${PURCHASE_KEY}${order.id}`;
    try {
      if (sessionStorage.getItem(dedupeKey)) return;
    } catch {
      /* */
    }

    const items = collectOrderItems(order);
    const cur = order.currency?.isoCode ?? currencyCode();
    const value = roundMoney(order.price?.totalPrice ?? 0);
    const tax = sumTaxes(order.price?.calculatedTaxes);
    const shipping = roundMoney(order.shippingTotal ?? 0);

    push("purchase", {
      ecommerce: {
        transaction_id: String(order.orderNumber ?? order.id),
        currency: cur,
        value,
        tax,
        shipping,
        items,
      },
    });

    try {
      sessionStorage.setItem(dedupeKey, "1");
    } catch {
      /* */
    }
  };

  return {
    push,
    trackEvent,
    trackFormSuccess,
    trackOnSuccess,
    trackViewItem,
    trackViewItemList,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
  };
}

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}
